import * as React from "react";
import { Row, Col, Button, Input } from 'antd';
import { observer } from "mobx-react";
import { Prompt } from "react-router";
import * as moment from "moment";

import { HeaderView } from 'src/app/shared/header.view';
import { TextView } from 'src/core/ui/text.view';
import { DateView } from "src/core/ui/date.view";
import { ClientItemEditorController } from 'src/app/crm/clients/parts/client-item-editor.controller';
import { MarketEditorController } from 'src/app/general/market/parts/market-editor.controller';
import { BrandEditorController } from 'src/app/general/brands/parts/brand-editor.controller';
import { AgentEditorController } from 'src/app/bookings/agents/parts/agent-editor.controller';
import { QuoteAgentModalController } from 'src/app/bookings/quotes/form/quote-agent-modal.controller';
import { QuoteClientModalController } from 'src/app/bookings/quotes/form/quote-client-modal.controller';

interface QuoteNewFormProps {
	model: QuoteItem;
	setBusy(isBusy: boolean);
	adults: QuotePaxItem[];
	children: QuotePaxItem[];
	infants: QuotePaxItem[];
	save: () => void,
	cancel: () => void,
	mode: ControlMode;
	onModeChanged: (mode: ControlMode) => void;
	onPaxCountChange: (type: 'Adult' | 'Children' | 'Infant', count: number) => void;
	onClientChange: (selectedClient: ClientItem) => void;
	onMarketChange: (selected: MarketSummary) => void;
	onAgentChange: (agent: AgentSummary) => Promise<void>;
	contextService: any;
	formatService: any;
	defaultLanguage:string
}

interface QuoteNewFormState {
	currencyName: string;
	showAgentForm: boolean;
	showClientForm: boolean;
}

@observer
export class QuoteNewForm extends React.Component<QuoteNewFormProps, QuoteNewFormState> {
	public state: QuoteNewFormState = {
		currencyName: "",
		showAgentForm: false,
		showClientForm: false
	};

	private clientIdPrev: number;

	private agentIdPrev: number;

	public render() {
		const { mode, onModeChanged, save, cancel, model, onClientChange, adults, children, infants, setBusy } = this.props;
		const { currencyName } = this.state;

		const currency = currencyName != "" ? ` (${currencyName})` : "";
		return (
			<div className="window window-normal">
				{this.renderAgentModal()}
				{this.renderClientModal()}
				<Prompt
					when={this.props.contextService.isDirty}
					message={location => `If you go to ${location.pathname}, all changes will be lost. Are you sure you want to continue?`}
				/>
				<Row>
					<Col xs={24}>
						<HeaderView
							title="Create a new quote"
							mode={mode}
							onModeChanged={onModeChanged}
							save={save}
							cancel={cancel}
							contextService={this.props.contextService}
						/>
					</Col>
				</Row>
				<div className="card p-3">
					<Row gutter={16}>
						<Col xs={24} lg={12}>
							<ClientItemEditorController
								required={model.clientId == null}
								onGetErrorMessage={() => model.validate(false, 'clientId')}
								setBusy={setBusy}
								mode={mode}
								value={model.clientId}
								onChanged={newVal => {
									if (newVal) {
										model.clientId = newVal.id;
										onClientChange(newVal);
									}
									
								}}
							>
								<Button
									type="primary"
									style={{ minWidth: 'auto', width: '40px' }}
									onClick={this.addClient}
									disabled={this.props.mode == 'display'}
									icon={"plus"}
								/>
							</ClientItemEditorController>

						</Col>
						<Col xs={24} lg={12}>
							<AgentEditorController
								setBusy={this.props.setBusy}
								mode={this.props.mode}
								value={model.agentId}
								onChanged={this.props.onAgentChange}
							>
								<Button
									type="primary"
									style={{ minWidth: 'auto', width: '40px' }}
									onClick={this.addAgent}
									disabled={this.props.mode == 'display'}
									icon={"plus"}
								/>
							</AgentEditorController>

						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={24} lg={12}>
							<MarketEditorController
								required
								label={`Market${currency}`}
								onGetErrorMessage={() => model.validate(false, 'marketId')}
								setBusy={setBusy}
								mode={mode}
								value={model.marketId}
								idAgent={model.agentId}
								onChanged={(newValue) => {
									if (newValue != null) {
										model.marketId = newValue.id;
										model.defaultTaxId = newValue.defaultTaxId;
									} else {
										model.marketId = null;
										model.defaultTaxId = null;
									}

									this.setState({
										currencyName: newValue.currencyDisplayName,
									});
									this.props.onMarketChange(newValue);
								}}
								afterLoad={this.afterMarketLoad}
							/>
						</Col>
						<Col xs={24} lg={12}>
							<BrandEditorController
								required
								onGetErrorMessage={() => model.validate(false, 'brandId')}
								setBusy={setBusy}
								mode={mode}
								idAgent={model.agentId}
								value={model.brandId}
								onChanged={(newValue) => {
									if (newValue != null) {
										model.brandId = newValue.id;
									} else {
										model.brandId = null;
									}
								}}
							/>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={24} lg={8}>
							<TextView label="Adults (greater than 12)" mode={mode} value={adults.length} onChanged={this.onChange("Adult")} />
						</Col>
						<Col xs={24} lg={8}>
							<TextView label="Children (3-12)" mode={mode} value={children.length} onChanged={this.onChange("Children")} />
						</Col>
						<Col xs={24} lg={8}>
							<TextView label="Infants (0-2)" mode={mode} value={infants.length} onChanged={this.onChange("Infant")} />
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={24} lg={8}>
							{this.renderPaxsBirthdates(adults)}
						</Col>
						<Col xs={24} lg={8}>
							{this.renderPaxsBirthdates(children)}
						</Col>
						<Col xs={24} lg={8}>
							{this.renderPaxsBirthdates(infants)}
						</Col>
					</Row>
				</div>
			</div>
		);
	}

	private renderAgentModal() {
		const { showAgentForm } = this.state;
		if (showAgentForm == null || showAgentForm == false) {
			return null;
		}
		return (
			<QuoteAgentModalController
				model={this.props.model}
				type="Person"
				closeDialog={this.closeAgentDialog}
				setBusy={this.props.setBusy}
				formatService={this.props.formatService}
				showModal={showAgentForm}
			/>
		);
	}

	private renderClientModal() {
		const { showClientForm } = this.state;
		if (showClientForm == null || showClientForm == false) {
			return null;
		}
		return (
			<QuoteClientModalController
				model={this.props.model}
				type="Person"
				closeDialog={this.closeClientDialog}
				setBusy={this.props.setBusy}
				formatService={this.props.formatService}
				showModal={showClientForm}
				defaultLanguage={this.props.defaultLanguage}
			/>
		);
	}

	private renderPaxsBirthdates(paxs: QuotePaxItem[]): React.ReactNode {
		const style = { width: "50%" };
		return paxs.map((p) => (
			<Input.Group compact key={p.id} className="mb-2">
				<TextView
					mode={this.props.mode}
					value={this.calculateAge(p)}
					type="number"
					onChanged={this.onChangeAge(p)}
					withoutFormWrapper={true}
					style={style}
				/>
				<DateView
					mode={this.props.mode}
					format="small-date"
					dateUsage="utc"
					value={p.dynamicProperties.birthDate}
					onChanged={this.onChangeBirthdate(p)}
					style={style}
					withoutFormWrapper={true}
				/>
			</Input.Group>
		));
	}

	private onChange = (type: 'Adult' | 'Children' | 'Infant') => (value: string) => {
		const count = parseInt(value, undefined);
		if (isNaN(count)) {
			return;
		}
		this.props.onPaxCountChange(type, count);
	}

	private afterMarketLoad = (selected: MarketSummary): void => {
		if (selected && this.state.currencyName != selected.currencyDisplayName) {
			this.setState({
				currencyName: selected.currencyDisplayName,
			});
		}
		if (selected) {
			this.props.model.defaultTaxId = selected.defaultTaxId;
		}
	}

	private addClient = () => {
		this.clientIdPrev = this.props.model.clientId;
		this.props.model.clientId = null;
		this.setState({
			showClientForm: true
		})
	}

	private closeClientDialog = (client: ClientItem, ev?: React.MouseEvent<HTMLButtonElement>) => {
		if (ev != null) {
			ev.preventDefault();
		}
		if (this.props.model.clientId == null)
			this.props.model.clientId = this.clientIdPrev;
		if (client !== null)
			this.props.onClientChange(client);
		this.setState({
			showClientForm: false,
		})
	}

	private addAgent = () => {
		this.agentIdPrev = this.props.model.agentId;
		this.props.model.agentId = null;
		this.setState({
			showAgentForm: true
		})
	}

	private closeAgentDialog = (ev?: React.MouseEvent<HTMLButtonElement>) => {
		if (ev != null) {
			ev.preventDefault();
		}
		if (this.props.model.agentId == null)
			this.props.model.agentId = this.agentIdPrev;
		this.setState({
			showAgentForm: false,
		})
	}

	private calculateAge(p: QuotePaxItem): number {
		return moment.utc().diff(moment(p.dynamicProperties.birthDate), 'year');
	}

	private onChangeAge = (p: QuotePaxItem) => (val: string) => {
		const age = parseInt(val);
		p.dynamicProperties.birthDate = moment.utc().subtract(age, "year").toDate();
		if (age > 12)
			p.dynamicProperties.foreName = "Adult";
		else if (age > 2)
			p.dynamicProperties.foreName = "Children";
		else
			p.dynamicProperties.foreName = "Infant";
	}

	private onChangeBirthdate = (p: QuotePaxItem) => (val: Date) => {
		p.dynamicProperties.birthDate = val;
		let age = moment.utc().diff(p.dynamicProperties.birthDate, "year");
		if (age > 12)
			p.dynamicProperties.foreName = "Adult";
		else if (age > 2)
			p.dynamicProperties.foreName = "Children";
		else
			p.dynamicProperties.foreName = "Infant";
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/quotes/form/quote-new-form.tsx